import { accountOwnerName } from "../../components/FinanceSelects";
import { CategoryIcon, currencySymbol, dateInputValue } from "../../lib/format";
import type {
  Account,
  ImportApplyResult,
  ImportBatch,
  ImportProvider,
  ImportPreviewRow,
  Member,
  TransactionType,
} from "../../lib/types";
export type ImportRowFilter = "all" | "ready" | "review" | "duplicate" | "ignored";
export type ImportRowFilterCriteria = {
  rowFilter: ImportRowFilter;
  searchQuery: string;
  searchType: TransactionType | "";
  searchDateFrom: string;
  searchDateTo: string;
};

export type StickyFilteredRows = {
  filterKey: string;
  rowIds: string[];
};

export type ImportSection = "records" | "categories" | "accounts" | "currencies";
export type RollbackSections = Record<"records" | "categories" | "accounts", boolean>;

export const providerOptions: { value: ImportProvider; label: string }[] = [
  { value: "bank_of_cyprus", label: "Bank of Cyprus (PDF)" },
  { value: "revolut", label: "Revolut (xlsx only)" },
  { value: "raiffeisen_russia", label: "Raiffeisen Russia (PDF)" },
  { value: "aibros_finance_file", label: "From file, AIbros finance (xlsx,csv)" },
];

export const importPollingIntervalMs = 3000;
export const importRowsPerPageOptions = [10, 20, 30, 50] as const;
export const defaultImportRowsPerPage = 30;
export const maxStatementUploadBytes = 10 * 1024 * 1024;
export const maxStatementUploadMegabytes = maxStatementUploadBytes / (1024 * 1024);
export const importStoppedError = "Import stopped";
export const transactionTypeLabels: Record<TransactionType, string> = {
  expense: "Expense",
  income: "Income",
  transfer: "Between accounts",
  correction: "Correction",
};

export const editableRowStatuses = ["pending", "confirmed", "ignored"] as const;

export function isTransactionType(value: unknown): value is TransactionType {
  return value === "expense" || value === "income" || value === "transfer" || value === "correction";
}

export function isEditableRowStatus(value: string) {
  return editableRowStatuses.includes(value as (typeof editableRowStatuses)[number]);
}

export function isImportedRow(row: ImportPreviewRow) {
  return String(row.status || "").toLowerCase() === "imported";
}

export function hasDuplicateRow(row: ImportPreviewRow) {
  return Boolean(row.duplicate_transaction_id);
}

export function missingCurrencyCode(row: ImportPreviewRow) {
  const message = row.error || "";
  const match = message.match(/Currency ([A-Z]{3}) is not enabled/);
  return match?.[1] || "";
}

export function isSystemRowStatus(status: string) {
  return ["imported", "error"].includes(status);
}

export function rowType(row: ImportPreviewRow): TransactionType {
  return isTransactionType(row.type) ? row.type : "expense";
}

export function rowDate(row: ImportPreviewRow) {
  const value = row.date || row.occurred_at;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  return dateInputValue(value);
}

export function dateToOccurredAt(value: string) {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export { currencySymbol };

export function statusLabel(status?: string | null) {
  if (!status) return "Pending";
  if (String(status).toLowerCase() === "pending") return "Queued";
  return status.replace(/_/g, " ").replace(/^\w/, (letter) => letter.toUpperCase());
}

export function isBatchCancelled(batch?: ImportBatch | null) {
  return String(batch?.status || "").toLowerCase() === "failed" && batch?.error === importStoppedError;
}

export function batchStatusLabel(batch?: ImportBatch | null, fallbackStatus?: string | null) {
  if (isBatchCancelled(batch)) return "Cancelled";
  if (isBatchRolledBack(batch?.status)) return "rolled-back";
  return importQueueLabel(batch) || statusLabel(batch?.status || fallbackStatus);
}

export function batchStatusColor(batch?: ImportBatch | null): "default" | "error" | "success" | "info" {
  if (isBatchCancelled(batch)) return "info";
  if (isBatchFailed(batch?.status)) return "error";
  if (isBatchRolledBack(batch?.status)) return "info";
  if (batch?.status === "applied") return "success";
  return "default";
}

export function importQueueLabel(batch?: ImportBatch | null) {
  if (!batch || String(batch.status || "").toLowerCase() !== "pending") return null;
  const position = Number(batch.queue_position);
  return Number.isFinite(position) && position > 0 ? `Queued #${position}` : "Queued";
}

export function importBatchTimestamp(batch: ImportBatch) {
  return batch.updated_at || batch.created_at || null;
}

export function formatBatchDate(batch: ImportBatch) {
  const value = importBatchTimestamp(batch);
  if (!value) return "Recent import";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Recent import";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function rowCount(batch: ImportBatch) {
  const count = batch.row_count ?? batch.rows_count ?? batch.total_rows ?? batch.rows?.length;
  return typeof count === "number" && Number.isFinite(count) ? count : null;
}

export function isBatchPolling(status?: string | null) {
  return ["pending", "uploading", "uploaded", "parsing", "processing", "applying"].includes(String(status || "").toLowerCase());
}

export function isBatchFailed(status?: string | null) {
  return ["failed", "error"].includes(String(status || "").toLowerCase());
}

export function isBatchApplied(status?: string | null) {
  return ["applied", "imported"].includes(String(status || "").toLowerCase());
}

export function isBatchRolledBack(status?: string | null) {
  return String(status || "").toLowerCase() === "rolled_back";
}

export function isBatchCompleted(status?: string | null) {
  return isBatchApplied(status) || isBatchRolledBack(status);
}

export function isImportableRow(row: ImportPreviewRow) {
  return ["pending", "confirmed"].includes(String(row.status || "pending").toLowerCase());
}

export function importableRowCount(rows: ImportPreviewRow[]) {
  return rows.filter(isImportableRow).length;
}

export function importedRowCount(rows: ImportPreviewRow[]) {
  return rows.filter(isImportedRow).length;
}

export function isRowUncertain(row: ImportPreviewRow) {
  if (isImportedRow(row) || hasDuplicateRow(row)) return false;
  const status = String(row.status || "").toLowerCase();
  if (status === "confirmed") return false;
  const type = rowType(row);
  if (type === "transfer" && status === "pending") return true;
  const confidence = typeof row.confidence === "number" ? row.confidence : null;
  const categoryConfidence = typeof row.category_confidence === "number" ? row.category_confidence : null;
  return Boolean(
    row.error ||
      (type !== "transfer" && type !== "correction" && !row.category_id && !row.category_entity_id) ||
      (confidence !== null && confidence < 0.75) ||
      (categoryConfidence !== null && categoryConfidence < 0.75),
  );
}

export function progressValue(batch?: ImportBatch | null) {
  const total = Number(batch?.progress_total ?? 0);
  const current = Number(batch?.progress_current ?? 0);
  if (Number.isFinite(total) && total > 0 && Number.isFinite(current)) {
    return Math.max(0, Math.min(100, (current / total) * 100));
  }
  const legacyProgress = Number(batch?.progress);
  if (!Number.isFinite(legacyProgress)) return null;
  return Math.max(0, Math.min(100, legacyProgress));
}

export function formatProgressPercent(progress: number | null) {
  if (progress === null) return null;
  return `${Math.round(progress)}%`;
}

export function formatImportEta(batch: ImportBatch | null | undefined, progress: number | null) {
  if (!batch || progress === null || progress <= 0 || progress >= 100) return null;
  const startedAt = new Date(batch.created_at || "").getTime();
  if (!Number.isFinite(startedAt)) return null;
  const elapsedMs = Date.now() - startedAt;
  if (elapsedMs < 3000) return "Estimating time...";
  const remainingMs = (elapsedMs / progress) * (100 - progress);
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return null;
  const remainingSeconds = Math.max(1, Math.round(remainingMs / 1000));
  if (remainingSeconds < 60) return `about ${remainingSeconds}s left`;
  const remainingMinutes = Math.max(1, Math.round(remainingSeconds / 60));
  return `about ${remainingMinutes} min left`;
}

export function appliedCount(result: ImportApplyResult, fallbackRows: ImportPreviewRow[]) {
  if (typeof result.applied_count === "number") return result.applied_count;
  if (typeof result.created_count === "number") return result.created_count;
  return (result.rows || fallbackRows).filter((row) => row.status === "imported").length;
}

export function skippedCount(result: ImportApplyResult) {
  if (typeof result.skipped_count === "number") return result.skipped_count;
  const rows = result.rows || [];
  const ignored = rows.filter((row) => row.status === "ignored").length;
  return ignored || null;
}

export function defaultTransferAccountId(accounts: Account[], sourceAccountId: string, currentTransferAccountId: string) {
  if (currentTransferAccountId && currentTransferAccountId !== sourceAccountId) return currentTransferAccountId;
  return accounts.find((account) => !account.is_archived && account.id !== sourceAccountId)?.id || "";
}

export function rowPreviewChip(row: ImportPreviewRow, uncertain: boolean): { label: string; color: "default" | "success" | "warning" } {
  if (isImportedRow(row)) return { label: "Imported", color: "success" };
  if (hasDuplicateRow(row)) return { label: "Already exists", color: "warning" };
  if (missingCurrencyCode(row)) return { label: "Missing currency", color: "warning" };
  if (row.has_conflict) return { label: "Review", color: "warning" };
  if (String(row.status || "").toLowerCase() === "ignored") return { label: "Ignored", color: "default" };
  return uncertain ? { label: "Review", color: "warning" } : { label: "Ready", color: "success" };
}

export function importRowFilterValue(row: ImportPreviewRow): ImportRowFilter {
  const status = String(row.status || "").toLowerCase();
  if (hasDuplicateRow(row)) return "duplicate";
  if (status === "ignored") return "ignored";
  if (isRowUncertain(row)) return "review";
  return "ready";
}

export function importRowFilterLabel(filter: ImportRowFilter) {
  const labels: Record<ImportRowFilter, string> = {
    all: "All",
    ready: "Ready",
    review: "Review",
    duplicate: "Duplicate",
    ignored: "Ignored",
  };
  return labels[filter];
}

export function importRowMatchesFilters(row: ImportPreviewRow, criteria: ImportRowFilterCriteria) {
  if (criteria.rowFilter !== "all" && importRowFilterValue(row) !== criteria.rowFilter) return false;
  if (criteria.searchQuery.trim()) {
    const query = criteria.searchQuery.trim().toLowerCase();
    const amountStr = row.amount != null ? String(row.amount) : "";
    const merchant = (row.merchant || "").toLowerCase();
    const note = (row.note || "").toLowerCase();
    const rawText = (row.raw_text || "").toLowerCase();
    if (!amountStr.includes(query) && !merchant.includes(query) && !note.includes(query) && !rawText.includes(query)) return false;
  }
  if (criteria.searchType && rowType(row) !== criteria.searchType) return false;
  if (criteria.searchDateFrom && rowDate(row) < criteria.searchDateFrom) return false;
  if (criteria.searchDateTo && rowDate(row) > criteria.searchDateTo) return false;
  return true;
}

export function importRowFilterKey(batchId: string, criteria: ImportRowFilterCriteria) {
  return JSON.stringify({
    batchId,
    rowFilter: criteria.rowFilter,
    searchQuery: criteria.searchQuery.trim(),
    searchType: criteria.searchType,
    searchDateFrom: criteria.searchDateFrom,
    searchDateTo: criteria.searchDateTo,
  });
}

export function recentBatchActionLabel(batch: ImportBatch, isFirst: boolean) {
  const status = String(batch.status || "").toLowerCase();
  if (isBatchApplied(status)) return "View import";
  if (isBatchRolledBack(status)) return "View rolled-back import";
  if (isBatchCancelled(batch)) return "View cancelled import";
  if (isBatchFailed(status)) return "View failed import";
  if (isBatchPolling(status)) return "View progress";
  if (["preview", "ready"].includes(status)) return "Continue review";
  return isFirst ? "Open latest import" : formatBatchDate(batch);
}

export function providerLabel(value: ImportProvider | string) {
  return providerOptions.find((option) => option.value === value)?.label || String(value || "Provider");
}

export function isAibrosFileProvider(value: ImportProvider | string) {
  return value === "aibros_finance_file";
}

export function isRevolutProvider(value: ImportProvider | string) {
  return value === "revolut";
}

export function isRaiffeisenRussiaProvider(value: ImportProvider | string) {
  return value === "raiffeisen_russia";
}

export function ProviderIcon({ value }: { value: ImportProvider | string }) {
  if (isAibrosFileProvider(value)) {
    return <img className="import-provider-file-icon" src="/static/bank-icons/xlsx_icon.png" alt="" />;
  }
  if (isRevolutProvider(value)) return <CategoryIcon icon="revolut" color="#0666eb" />;
  if (isRaiffeisenRussiaProvider(value)) return <CategoryIcon icon="raiffeisen" color="#fff500" />;
  return <CategoryIcon icon="boc" color="#00b9d1" />;
}

export function accountHistoryLabel(account: Account, members: Member[]) {
  const ownerName = accountOwnerName(account, members);
  return ownerName ? `${account.name} · ${ownerName}` : account.name;
}

export function ProviderOption({ value, label }: { value: ImportProvider | string; label: string }) {
  return (
    <span className="import-provider-option">
      <span className="import-provider-logo" aria-hidden="true">
        <ProviderIcon value={value} />
      </span>
      <span>{label}</span>
    </span>
  );
}
